// PRD 06 §2.1 — 주기별 보기 tab body. Renders the info banner + a ModuleCard
// per `Module`. The banner copy is updated in Slice 8 to mention the drag grip.

import { useMemo } from 'react';
import { Info } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';

import { ModuleCard } from './ModuleCard';
import type { Module, ProItemRef } from '../types/pro-deployment.types';

interface ModuleCardListProps {
  modules: Module[];
  items: ProItemRef[];
  diseaseFilter: string;
  // FEATURE_025 — whether an item belongs to the selected dropdown row.
  isVisible: (item: ProItemRef) => boolean;
  // FEATURE_025 — whether a shown item is 조회 전용 on the selected row. Omitted → none is.
  isViewOnly?: (item: ProItemRef) => boolean;
}

export function ModuleCardList({
  modules,
  items,
  diseaseFilter,
  isVisible,
  isViewOnly,
}: ModuleCardListProps) {
  const { t } = useTranslation('pro-deployment');
  const itemDict = useMemo(() => new Map(items.map((i) => [i.pid, i])), [items]);

  // FEATURE_025 — every dropdown row shares ONE module set, and each card shows only the row's items. A module
  // that holds items but none the selected row shows is left out for that row: its card would say nothing. A
  // module holding no item at all is still drawn, because its card carries the reason the deploy is blocked —
  // the same "truly empty" count the page header uses (a member missing from the dictionary shows nowhere).
  const visibleModules = useMemo(
    () =>
      modules.filter(
        (m) =>
          m.itemIds.length === 0 ||
          m.itemIds.some((pid) => {
            const item = itemDict.get(pid);
            return item ? isVisible(item) : false;
          }),
      ),
    [modules, itemDict, isVisible],
  );

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '10px 14px',
          marginBottom: 'var(--space-4)',
          background: '#EFF6FF',
          border: '1px solid #BFDBFE',
          borderRadius: 'var(--radius-sm)',
          fontSize: 13,
          color: '#1E40AF',
        }}
      >
        <Info size={16} style={{ flexShrink: 0 }} aria-hidden />
        <span>
          <Trans
            i18nKey="module.banner"
            t={t}
            components={{ strong: <strong /> }}
          />
        </span>
      </div>
      {visibleModules.length === 0 ? (
        <div
          className="card"
          style={{
            padding: '48px 24px',
            textAlign: 'center',
            color: 'var(--muted-foreground)',
            fontSize: 'var(--font-size-sm)',
          }}
        >
          {t('state.modulesEmpty')}
        </div>
      ) : (
        visibleModules.map((module) => (
          <ModuleCard
            key={module.mid}
            module={module}
            itemDict={itemDict}
            diseaseFilter={diseaseFilter}
            isVisible={isVisible}
            isViewOnly={isViewOnly}
          />
        ))
      )}
    </div>
  );
}
